const API_BASE = import.meta.env.VITE_API_BASE || '/api';

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
}

export interface ChatResponse {
  sessionId: string;
  message: string;
  suggestions?: string[];
}

export interface VoiceResponse {
  sessionId: string;
  transcription: string;
  responseText: string;
  responseAudio: string;
  suggestions?: string[];
}

export interface UserProgress {
  userId: string;
  totalSessions: number;
  topicsStudied: string[];
  quizzesTaken: number;
  quizzesPassedCount: number;
  studyStreak: number;
  lastStudyDate: string;
  achievements: string[];
}

export interface Quiz {
  id: string;
  topic: string;
  questions: QuizQuestion[];
  createdAt: number;
}

export interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

export const api = {
  async chat(
    userId: string,
    message: string,
    sessionId?: string,
    topic?: string
  ): Promise<ChatResponse> {
    const response = await fetch(`${API_BASE}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, message, sessionId, topic }),
    });

    if (!response.ok) {
      throw new Error('Failed to send message');
    }

    return response.json();
  },

  async sendVoice(
    userId: string,
    audioData: string,
    format: 'webm' | 'mp3' | 'wav',
    sessionId?: string,
    topic?: string
  ): Promise<VoiceResponse> {
    const response = await fetch(`${API_BASE}/voice`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, audioData, format, sessionId, topic }),
    });

    if (!response.ok) {
      throw new Error('Failed to process voice');
    }

    return response.json();
  },

  async generateQuiz(topic: string, numQuestions?: number): Promise<Quiz> {
    const response = await fetch(`${API_BASE}/quiz/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ topic, numQuestions }),
    });

    if (!response.ok) {
      throw new Error('Failed to generate quiz');
    }

    return response.json();
  },

  async submitQuiz(
    userId: string,
    quizId: string,
    answers: number[]
  ): Promise<{ score: number; progress: UserProgress }> {
    const response = await fetch(`${API_BASE}/quiz/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, quizId, answers }),
    });

    if (!response.ok) {
      throw new Error('Failed to submit quiz');
    }

    return response.json();
  },

  async getProgress(userId: string): Promise<UserProgress> {
    const response = await fetch(`${API_BASE}/progress?userId=${userId}`);

    if (!response.ok) {
      throw new Error('Failed to get progress');
    }

    return response.json();
  },

  async startSession(
    userId: string,
    topic: string,
    duration: number,
    includeQuiz: boolean
  ): Promise<{ sessionId: string; message: string }> {
    const response = await fetch(`${API_BASE}/session/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, topic, duration, includeQuiz }),
    });

    if (!response.ok) {
      throw new Error('Failed to start session');
    }

    return response.json();
  },

  async endSession(
    userId: string,
    sessionId: string,
    topic: string
  ): Promise<{ message: string; progress: UserProgress }> {
    const response = await fetch(`${API_BASE}/session/end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, sessionId, topic }),
    });

    if (!response.ok) {
      throw new Error('Failed to end session');
    }

    return response.json();
  },
};
